import { useInfiniteQuery } from "@tanstack/react-query";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";

import { getGetMessagesQueryKey, getMessages } from "@/api/orval/user/user";
import type { DisplayPatientTextMessage } from "@/api/orval/model";
import { useTranslation } from "@/core/i18n";
import { Button } from "atoms/button/Button";
import ChevronLeft from "atoms/icon/icons/chevron_left.svg";
import { EmptyState } from "molecules/empty-state/EmptyState";
import TopNav from "molecules/navbars/TopNav";
import { readMessageIds } from "modules/messages/readMessages";
import { useUser } from "modules/user";

import { AccountDestinationSkeleton } from "../AccountDestinationSkeleton";

const FIRST_PAGE = 1;

type MessageGroup = { key: "thisMonth" | "earlier"; messages: DisplayPatientTextMessage[] };

const groupByRecency = (messages: DisplayPatientTextMessage[]): MessageGroup[] => {
  const thisMonth = messages.filter((message) => dayjs(message.sentOn).isSame(dayjs(), "month"));
  const earlier = messages.filter((message) => !dayjs(message.sentOn).isSame(dayjs(), "month"));

  return [
    { key: "thisMonth" as const, messages: thisMonth },
    { key: "earlier" as const, messages: earlier },
  ].filter((group) => group.messages.length > 0);
};

export const MessagesPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useUser();

  const { data, error, isError, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery(
    {
      initialPageParam: FIRST_PAGE,
      queryKey: [...getGetMessagesQueryKey(), "infinite"],
      queryFn: ({ pageParam, signal }) => getMessages({ page: pageParam }, undefined, signal),
      getNextPageParam: ({ page, totalPages }) => (page < totalPages ? page + 1 : undefined),
    },
  );

  if (isError) throw error;

  if (!data || !user) return <AccountDestinationSkeleton title={t("messages.header")} />;

  const messages = data.pages.flatMap((page) => page.items);
  const readIds = readMessageIds(user.id);

  return (
    <div className="relative flex min-h-screen flex-col bg-one">
      <TopNav
        startIconSVG={ChevronLeft}
        onStartIconClick={() => navigate("/account")}
        showHeaderItems={false}
      >
        <div className="flex max-h-[32px] items-center">
          <div className="flex w-full items-center justify-center text-sub2 uppercase text-primary">
            {t("messages.header")}
          </div>
        </div>
      </TopNav>

      {messages.length === 0 ? (
        <EmptyState
          title={t("messages.emptyTitle")}
          body={t("messages.emptyBody")}
          buttonText={t("messages.emptyAction")}
          onClick={() => navigate("/account")}
        />
      ) : (
        <div className="flex flex-col gap-6 px-4 py-6 desk:px-0 desk:py-0">
          {groupByRecency(messages).map((group) => (
            <section key={group.key} className="flex flex-col gap-2">
              <h2 className="px-2 text-sub2 uppercase text-secondary">
                {t(group.key === "thisMonth" ? "messages.thisMonth" : "messages.earlier")}
              </h2>
              <ul className="flex flex-col gap-2">
                {group.messages.map((message) => {
                  const isUnread = !readIds.has(message.id);

                  return (
                    <li key={message.id}>
                      <button
                        type="button"
                        className="flex w-full items-start gap-3 rounded-xl bg-white p-4 text-left shadow-sm transition-opacity active:opacity-60"
                        onClick={() => navigate(`/account/messages/${message.id}`)}
                      >
                        <span
                          role={isUnread ? "img" : undefined}
                          aria-label={isUnread ? t("messages.unread") : undefined}
                          className={`mt-2 h-2 w-2 flex-none rounded-full ${isUnread ? "bg-brand-color" : ""}`}
                        />
                        <span className="flex min-w-0 flex-1 flex-col gap-2">
                          <span
                            className={`line-clamp-3 text-body1 text-primary ${isUnread ? "font-medium" : ""}`}
                          >
                            {message.message}
                          </span>
                          <span className="text-sub2 text-secondary">
                            {dayjs(message.sentOn).format("ll")}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </section>
          ))}

          {hasNextPage && (
            <div className="flex justify-center">
              <Button
                style="secondary"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
              >
                {t("messages.loadMore")}
              </Button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
